/**
 * acceptedView.js: IDI-416 (ruled by Adrian, 2026-10-14).
 *
 * Once a quote is signed or converted, the ONE record of what the customer
 * accepted is its AcceptedConfiguration snapshot (served by GET /v1/quotes/:id
 * as `quote.acceptedConfiguration`). Every screen that shows an accepted
 * quote's lines or total reads it through here, so the status, total, list
 * Amount, document, Record Decision and the job's Quote Items agree, whatever
 * the builder holds now.
 *
 * Before: each screen read the MUTABLE quote (`quote.line_items`,
 * `total_amount`), so a quote edited after signing showed different lines and
 * totals than the job and the signature.
 */

/** True when the quote carries a readable snapshot. */
export function hasAcceptedSnapshot(quote) {
  const cfg = quote && quote.acceptedConfiguration;
  return !!(cfg && !cfg.unavailable && Array.isArray(cfg.line_items));
}

/** The snapshot could not be read (shown as a notice, never as $0 or the live quote). */
export function acceptedSnapshotUnavailable(quote) {
  return !!(quote && quote.acceptedConfiguration && quote.acceptedConfiguration.unavailable);
}

/**
 * A snapshot line in the shape every reader of quote lines understands:
 * camelCase AND snake_case for the fields the builder/document/job panels
 * read (productLabel/item_name, areaLabel, subAreaLabel, sqft, lineTotal).
 */
export function snapshotLineToQuoteLine(l) {
  if (!l) return l;
  return {
    ...l,
    item_name: l.item_name || '',
    productLabel: l.item_name || '',
    name: l.item_name || '',
    areaId: l.area_id || l.areaId || '',
    area_id: l.area_id || l.areaId || '',
    areaLabel: l.area_label || l.areaLabel || '',
    area_label: l.area_label || l.areaLabel || '',
    subAreaId: l.sub_area_id || l.subAreaId || '',
    sub_area_id: l.sub_area_id || l.subAreaId || '',
    subAreaLabel: l.sub_area_label || l.subAreaLabel || '',
    sub_area_label: l.sub_area_label || l.subAreaLabel || '',
    sqft: l.sqft ?? null,
    lineTotal: l.line_total ?? 0,
    line_total: l.line_total ?? 0,
  };
}

/**
 * The quote as it was ACCEPTED: the snapshot's lines and money laid over the
 * quote record, with flat lines only (the snapshot has resolved alternates and
 * options, so the areas tree is dropped rather than shown with stale lines).
 * Returns the quote unchanged when there is no snapshot.
 */
export function acceptedQuoteView(quote) {
  if (!hasAcceptedSnapshot(quote)) return quote;
  const cfg = quote.acceptedConfiguration;
  const lines = cfg.line_items.map(snapshotLineToQuoteLine);
  return {
    ...quote,
    line_items: lines,
    lineItems: lines,
    areas: [],
    subtotal: cfg.subtotal,
    discountAmount: cfg.discount_amount,
    discount_amount: cfg.discount_amount,
    taxAmount: cfg.tax_amount,
    tax_amount: cfg.tax_amount,
    totalAmount: cfg.total_cents,
    total_amount: cfg.total_cents,
    total: cfg.total_cents,
    isAcceptedView: true,
  };
}
